import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { NavigationEnd, Router } from "@angular/router";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { Tool } from "src/app/models/Tool";
import { ToolFilter } from "src/app/models/ToolFilter";
import { CompanyService } from "src/app/services/company.service";
import { ModalComponent, ModalService } from "src/app/services/modal.service";
import { ToolFilterService } from "src/app/services/tool-filter.service";

import { ToolService } from "../../services/tool.service";
import { Company } from "./../../models/Company";
import { PoBasketService } from "./../../services/po-basket.service";
import { RaBasketService } from "./../../services/ra-basket.service";

export type SearchModalData = {
  company: Company;
  quickSearch: string;
  filter: ToolFilter;
};

@Component({
  selector: "search-modal",
  templateUrl: "./search-modal.component.html",
  styleUrls: ["./search-modal.component.scss"],
  standalone: false,
})
export class SearchModalComponent
  implements OnDestroy, AfterViewInit, ModalComponent<SearchModalComponent, SearchModalData>
{
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  private _subscriptions$: Subscription[] = [];
  readonly localizationConstants = LocalizationConstants;

  quickSearch: string;
  dataSource = new MatTableDataSource<Tool>([]);
  toolsTotal: number;
  faSpinner = faSpinner;
  isLoading: boolean;
  company: Company;
  pageSize: number = 8;

  columns = [
    // Intentionally exchanged toolId with serialNumber
    { key: "serialNumber", title: LocalizationConstants.TOOL_TOOL_ID },
    { key: "toolId", title: LocalizationConstants.TOOL_SERIAL_NUMBER },
    { key: "modelNumber", title: LocalizationConstants.TOOL_MODEL_NUMBER },
  ];

  columnKeys = this.columns.map((column) => column.key);

  constructor(
    private toolsService: ToolService,
    public companyService: CompanyService,
    public raBasket: RaBasketService,
    public poBasket: PoBasketService,
    public modalService: ModalService,
    private router: Router,
    private toolFilterService: ToolFilterService,
    // dialogRef needed here to implement interface for ModalService - taking types from it
    public dialogRef: MatDialogRef<SearchModalComponent, void>,
    @Inject(MAT_DIALOG_DATA) public data: SearchModalData,
  ) {
    this.company = this.data.company;
    this.quickSearch = this.data.quickSearch;
    this._subscriptions$.push(this.getByFilter(this.data.filter));
    this._subscriptions$.push(this.onRouteChange());
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onRouteChange() {
    return this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.modalService.close();
      }
    });
  }

  /** Redirects to the tool details page with given companyId and serialNumber.
   * Here first it redirects to root and only then redirects to the ToolDetails
   * component. This is making sure that if search modal is used from ToolDetails
   * page, and user selects a different tool the redirection happens. Otherwise
   * from Angular point of view it's the same route and no reload of the page happens.
   * Also it's easier to handle this edge case from SearchComponent, since ToolDetails
   * has nested child router-outlets, which causes quite issue to correctly propagate the
   * page reload and propagating to all related nested components.
   * Credits to: https://stackoverflow.com/a/49509706/4538875
   */
  public redirectTo(companyId: string, serialNumber: string): void {
    this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
      this.router.navigate([RouteNameConstants.TOOLS_DETAILS_BASE, companyId, serialNumber]);
    });
  }

  getByFilter(filter: ToolFilter) {
    return this.toolsService.getByFilter(filter).subscribe(
      (response) => {
        if (!response) return;

        this.isLoading = false;
        this.dataSource.data = response["items"];
        this.toolsTotal = response["totalCount"];
      },
      () => (this.isLoading = false),
    );
  }

  onSubmit() {
    if (!this.quickSearch) return;
    const filter = this.toolFilterService.create();
    filter.search = this.quickSearch;

    this.isLoading = true;
    this._subscriptions$.push(this.getByFilter(filter));
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((a) => a.unsubscribe());
  }
}
